import { Middleware } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import { addExpense, isExpensesSliceAction, updateExpense } from "../expensesSlice";
import { updateIncident } from "../incidentsSlice";

/**
 * Closing an incident says "I'm done with this one" - it stops generating todos and
 * drops out of the open incidents list. Attaching a new expense to it says the
 * opposite, so this middleware reopens the incident whenever an expense lands on it.
 *
 * Only *newly* attached expenses reopen an incident: editing an expense that already
 * belongs to the closed incident (fixing an amount, attaching a receipt) leaves it
 * closed.
 */
export const reopenClosedIncidentMiddleware: Middleware<{}, RootState> = (store) => (next) => (action) => {
  if (!isExpensesSliceAction(action)) {
    return next(action);
  }

  switch (action.type) {
    case addExpense.type:
      maybeReopenIncident(store, action.payload.incident_id);
      return next(action);

    case updateExpense.type: {
      if (!('incident_id' in action.payload) || !action.payload.incident_id) {
        return next(action);
      }

      // Only a move onto a different incident counts as attaching an expense to it
      const previousExpense = store.getState().expenses.expenses.find((e) => e.id === action.payload.id);
      if (previousExpense?.incident_id !== action.payload.incident_id) {
        maybeReopenIncident(store, action.payload.incident_id);
      }

      return next(action);
    }

    default:
      return next(action);
  }
}

type Store = {
  dispatch: (action: any) => void,
  getState: () => RootState
}

function maybeReopenIncident(store: Store, incidentId: string | null | undefined) {
  if (!incidentId) {
    return;
  }

  const incident = store.getState().incidents.incidents.find((i) => i.id === incidentId);
  if (!incident || !incident.closed_at) {
    return;
  }

  const now = new Date().toISOString();
  store.dispatch(updateIncident({
    id: incident.id,
    closed_at: null,
    updated_at: now,
  }));

  return true;
}
